/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import './HeaderScrollNavigationBar.css';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, ButtonItem } from '../../components/button/Button';
import { IconItem } from '../../components/icon/Icon';
import { Link } from '../../components/link/Link';

type HeaderScrollNavigationBarProps = {
  actionButton?: ButtonItem;
  activeTab: {
    code: string;
  };
  label: {
    id: string;
    text: string;
  };
  links: {
    id: string;
    label: string;
    href?: string;
    isActive: boolean;
    isVisible: boolean;
    icon?: IconItem;
    onClick?: () => void;
  }[];
  changeMiddleBarVisibility?: () => void;
  healthAreaLabel?: string;
  second?: boolean;
};

export function HeaderScrollNavigationBar({
  actionButton,
  activeTab,
  label,
  links,
  changeMiddleBarVisibility,
  healthAreaLabel,
  second,
}: HeaderScrollNavigationBarProps) {
  const { t } = useTranslation();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const { pathname } = useLocation();
  const isGoalsPage = pathname.includes('goals');

  const SCROLL_TOLERANCE = 4;

  const checkScroll = () => {
    if (navRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
      setCanScrollLeft(scrollLeft > SCROLL_TOLERANCE);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_TOLERANCE);
    }
  };

  useEffect(() => {
    const navigationBar = navRef.current;
    if (!navigationBar) return;

    const checkScrollDebounced = () => {
      requestAnimationFrame(checkScroll);
    };

    const resizeObserver = new ResizeObserver(checkScrollDebounced);
    resizeObserver.observe(navigationBar);

    const mutationObserver = new MutationObserver(checkScrollDebounced);
    mutationObserver.observe(navigationBar, { childList: true, subtree: true });

    window.addEventListener('resize', checkScrollDebounced);
    checkScrollDebounced();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', checkScrollDebounced);
    };
  }, [links]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!links.length) return;

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();

      const visibleLinks = links.filter((link) => link.isVisible);
      const currentIndex = focusedIndex ?? 0;
      const nextIndex =
        e.key === 'ArrowRight'
          ? (currentIndex + 1) % visibleLinks.length
          : (currentIndex - 1 + visibleLinks.length) % visibleLinks.length;

      setFocusedIndex(nextIndex);
      itemRefs.current[nextIndex]?.focus();
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const el = e.currentTarget as HTMLAnchorElement;
      el.click();
    }
  };

  const scrollByAmount = (amount: number) => {
    if (navRef.current) {
      navRef.current.scrollBy({ left: amount, behavior: 'smooth' });

      setTimeout(() => {
        checkScroll();
      }, 300);
    }
  };

  const getChapterNavLabel = () => {
    if (second && healthAreaLabel === t('for.you.page.title')) {
      return isGoalsPage
        ? `${t('nav.pages.goals')} ${t('nav.page.submenu')}`
        : `${t('SD005')} ${t('nav.page.submenu')}`;
    }

    return healthAreaLabel
      ? `${healthAreaLabel} ${t('nav.page.submenu')}`
      : t('nav.pages.healthAreas');
  };

  return (
    <div id="header-scroll-bar">
      <div className="header-navigation-scroll-bar-wrapper">
        {links && (
          <div className="header-navigation-scroll-bar-links">
            {(canScrollLeft || canScrollRight) && (
              <Button
                variant="icon-only"
                color="white"
                isDisabled={!canScrollLeft}
                onClick={() => scrollByAmount(-100)}
                icon={{
                  faIconDefinition: faChevronLeft,
                  color: 'white',
                  size: 'lg',
                }}
                isDecorative
              />
            )}
            <nav
              id="chapternav"
              className="chapternav theme-dark"
              aria-label={getChapterNavLabel()}
            >
              <div className="chapternav-wrapper">
                <ul
                  className={`chapternav-items ${canScrollLeft ? 'scroll-left' : ''} ${canScrollRight ? 'scroll-right' : ''}`}
                  ref={navRef}
                >
                  {links
                    .filter((link) => link.isVisible)
                    .map((link) => {
                      const visibleIndex = links.filter((l) => l.isVisible).indexOf(link);
                      const isActive = link.id === activeTab?.code;

                      return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <li
                          key={link.id}
                          className={`chapternav-item header-navigation-scroll-bar-link ${isActive ? 'is-active' : ''}`}
                          onFocus={() => setFocusedIndex(visibleIndex)}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (link.href) {
                              if (link.href?.startsWith('#')) {
                                const el = document.getElementById(link.href.slice(1));
                                if (el) {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                                changeMiddleBarVisibility?.();
                              } else {
                                navigate(link.href);
                                changeMiddleBarVisibility?.();
                              }
                            } else if (link.onClick) {
                              link.onClick();
                            }
                          }}
                        >
                          <Link
                            ariaCurrent={isActive ? 'page' : undefined}
                            ariaLabel={`${link.label} ${isActive ? t('nav.page.selectedTab') : ''}`}
                            href={link.href}
                            text={link.label}
                            icon={link.icon}
                            color={isActive ? 'white' : 'gray'}
                            colorOnHover={isActive ? 'white' : 'gray'}
                            className="nav-link-item"
                            onKeyDown={handleKeyDown}
                            ref={(el) => (itemRefs.current[visibleIndex] = el)}
                          />
                        </li>
                      );
                    })}
                </ul>
              </div>
            </nav>
            {(canScrollRight || canScrollLeft) && (
              <Button
                variant="icon-only"
                color="white"
                isDisabled={!canScrollRight}
                onClick={() => scrollByAmount(100)}
                icon={{
                  faIconDefinition: faChevronRight,
                  color: 'white',
                  size: 'lg',
                }}
                isDecorative
              />
            )}
          </div>
        )}
        {actionButton && (
          <div
            className={`header-navigation-scroll-bar-button ${isGoalsPage ? 'top-layer-900' : 'top-layer-600'}  ${!links ? 'without-links' : 'with-links'}`}
          >
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Button {...actionButton} />
          </div>
        )}
      </div>
    </div>
  );
}
